/** A heading of the note, for the outline. `line` counts from 0 in the body. */
export interface Heading {
  level: number;
  text: string;
  line: number;
}

const FENCE = /^ {0,3}(`{3,}|~{3,})/;
const ATX = /^ {0,3}(#{1,6})[ \t]+(.*?)(?:[ \t]+#+)?[ \t]*$/;

/** Headings as plain words: links, emphasis and code marks are dropped. */
function plain(text: string): string {
  return text
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, "$1")
    .replace(/[*_~`]+/g, "")
    .replace(/\s+/g, " ")
    .trim();
}

/** The `#` headings of a Markdown body, leaving out anything inside a fenced code block. */
export function outlineOf(body: string): Heading[] {
  const out: Heading[] = [];
  let fence: string | null = null;
  const lines = body.split("\n");
  for (let line = 0; line < lines.length; line += 1) {
    const text = lines[line]!;
    const open = FENCE.exec(text);
    if (fence) {
      if (
        open &&
        open[1]![0] === fence[0] &&
        open[1]!.length >= fence.length &&
        !/\S/.test(text.slice(open[0].length))
      ) {
        fence = null;
      }
      continue;
    }
    if (open) {
      fence = open[1]!;
      continue;
    }
    const heading = ATX.exec(text);
    const words = heading ? plain(heading[2]!) : "";
    if (heading && words) out.push({ level: heading[1]!.length, text: words, line });
  }
  return out;
}

/** The heading the reader is in: the last one at or above this line, or -1 before the first. */
export function headingAt(headings: Heading[], line: number): number {
  let found = -1;
  for (let i = 0; i < headings.length; i += 1) {
    if (headings[i]!.line <= line) found = i;
    else break;
  }
  return found;
}

/** Link-address form of a title: plain a-z, 0-9 and dashes only (accents dropped, other scripts left out). */
export function anchorOfTitle(title: string): string {
  return title
    .normalize("NFD")
    .replace(/\p{M}+/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9\s_-]/g, "")
    .trim()
    .replace(/[\s-]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** The address each heading gets as a link target; empty ones are "section", repeats are numbered. */
export function anchorsOf(headings: Heading[]): string[] {
  const seen = new Map<string, number>();
  return headings.map((h) => {
    const base = anchorOfTitle(h.text) || "section";
    const n = seen.get(base) ?? 0;
    seen.set(base, n + 1);
    return n === 0 ? base : `${base}-${n}`;
  });
}

/** The index of the heading a `#address` points to, or -1. Accents in the address are forgiven. */
export function headingByAnchor(headings: Heading[], address: string): number {
  let wanted = address.replace(/^#/, "");
  try {
    wanted = decodeURIComponent(wanted);
  } catch {}
  return anchorsOf(headings).indexOf(anchorOfTitle(wanted) || "section");
}
